import { useState } from 'react';
import { HandHelping, BarChart3, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { MarketerProjects } from '@/components/marketer/MarketerProjects';
import { MarketerMonthlyReport } from '@/components/marketer/MarketerMonthlyReport';
import { MarketerCases } from '@/components/marketer/MarketerCases';

// View switcher: three views, following the same inline-flex segmented-control pattern
// CoordinatorDashboard.tsx uses for its own `activeView` union (its switcher renders at
// lines ~2330-2372, e.g. `activeView === 'links'` there — read for structure, not modified
// here). Projects stays the default view. 'cases' (M4) is a read-only view — see
// MarketerCases.tsx for the no-mutating-controls contract.
type MarketerView = 'projects' | 'report' | 'cases';

export function MarketerDashboard() {
  const { t, dir } = useLanguage();
  const [activeView, setActiveView] = useState<MarketerView>('projects');

  return (
    <div className="space-y-4 md:space-y-6" dir={dir}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          <h1 className="text-2xl font-bold">{dir === 'rtl' ? 'مسوق' : 'Marketer'}</h1>
          <p className="text-muted-foreground">
            {dir === 'rtl' ? 'إنشاء وإدارة مشاريع التبرع' : 'Create and manage fundraising projects'}
          </p>
        </div>
        <div className="inline-flex items-center rounded-lg border bg-muted p-0.5 gap-0.5 self-start">
          <button
            onClick={() => setActiveView('projects')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'projects' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <HandHelping className="h-4 w-4" />
            {t('dashboard.marketer.views.projects')}
          </button>
          <button
            onClick={() => setActiveView('report')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'report' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <BarChart3 className="h-4 w-4" />
            {t('dashboard.marketer.views.report')}
          </button>
          <button
            onClick={() => setActiveView('cases')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'cases' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <FileText className="h-4 w-4" />
            {t('dashboard.marketer.views.cases')}
          </button>
        </div>
      </div>

      {activeView === 'projects' && <MarketerProjects />}
      {activeView === 'report' && <MarketerMonthlyReport />}
      {activeView === 'cases' && <MarketerCases />}
    </div>
  );
}
